import { FC, useCallback, useEffect } from 'react';

import useDC from '@eneco/dc/useDC';
import { typedYupResolver } from '@eneco/hooks/form';
import { I18nProvider, useTranslation } from '@eneco/i18n';
import { Button, Form, Heading, InputNumber, InputText, Skeleton, Stack, Text } from '@eneco/sparky';
import { useTracking } from '@eneco/tracking';
import { useForm } from 'react-hook-form';

import { ReadingStepErrors } from './components/ReadingStepErrors';
import { readingIdStepSchema } from './ReadingIdStep.schema';
import { useMeterReadingsFlow } from '../state/MeterReadingsFlow.machine';
import { ReadingContext } from '../state/MeterReadingsFlow.types';
import { Steps } from '../tracking';

export type FormValues = Pick<ReadingContext, 'readingId' | 'houseNumber'>;

export const ReadingIdStep: FC = () => {
  const { label } = useDC();

  return (
    <I18nProvider dictionary={locale => import(`./content/ReadingIdStep.${label}.${locale}.json`)}>
      <ReadingIdStepComponent />
    </I18nProvider>
  );
};

export const ReadingIdStepComponent: FC = () => {
  const { t, isLoading } = useTranslation();
  const { trackFunnelStart, trackFunnelStep, scope } = useTracking();
  const { state, send } = useMeterReadingsFlow();

  useEffect(() => {
    if (!state.context.funnelStarted) return;

    trackFunnelStep({
      step: Steps.READING_ID,
      funnel: state.context.funnelName,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOnFocus = () => {
    if (state.context.funnelStarted) return;
    const funnelName = `${scope} - periodiek`;
    trackFunnelStart({ step: Steps.READING_ID, funnel: funnelName });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const payload: any = { type: 'FUNNEL_START', funnelName };
    send(payload);
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: typedYupResolver<FormValues>(readingIdStepSchema),
    defaultValues: {
      readingId: state.context.readingId || undefined,
      houseNumber: state.context.houseNumber || undefined,
    },
  });

  const onSubmit = useCallback(
    ({ readingId, houseNumber }: FormValues) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const payload: any = { type: 'NEXT', readingId, houseNumber };
      send(payload);
    },
    [send],
  );

  if (isLoading) return <Skeleton height={210} />;

  return (
    <Form onFocus={handleOnFocus} onSubmit={handleSubmit(onSubmit)}>
      <Stack gap="6">
        <Stack gap="2">
          <Heading as="h1" size="M">
            {t('title')}
          </Heading>
          <Text>{t('content')}</Text>
        </Stack>
        <Stack gap="10">
          <Stack gap="5" direction="row">
            <Stack.Item>
              <InputText
                {...register('readingId')}
                error={errors.readingId?.message && t(`readingIdFormField.${errors.readingId.message}Message`)}
                label={t('readingIdFormField.label')}
                placeholder={t('readingIdFormField.placeholder')}
                defaultValue={state.context.readingId || undefined}
              />
            </Stack.Item>
            <Stack.Item>
              <InputNumber
                {...register('houseNumber')}
                error={errors?.houseNumber?.message && t('houseNumberFormField.requiredMessage')}
                label={t('houseNumberFormField.label')}
                placeholder={t('houseNumberFormField.placeholder')}
                formatOptions={{ useGrouping: false }}
                defaultValue={state.context.houseNumber || undefined}
              />
            </Stack.Item>
          </Stack>

          <ReadingStepErrors />

          <Stack.Item>
            <Button type="submit" isLoading={isLoading}>
              {t('submitButtonText')}
            </Button>
          </Stack.Item>
        </Stack>
      </Stack>
    </Form>
  );
};
